import { useState, useRef } from 'react';
import { ArrowLeft, Send, Sparkles, Search, Camera, AlertCircle, Eye, X, History, BookOpen } from 'lucide-react';
import { Wisdom } from '../data/wisdom';
import { useSessionLogger } from '../hooks/useSessionLogger';
import { useProfile } from '../hooks/useProfile';
import logo from '../assets/logo.png';

interface LifelineProps {
  onBack: () => void;
}

export function Lifeline({ onBack }: LifelineProps) {
  const { profile } = useProfile();
  const [input, setInput] = useState('');
  const [result, setResult] = useState<Wisdom | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReasoning, setShowReasoning] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [compareBible, setCompareBible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { logs, logEvent, clearLogs } = useSessionLogger();

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSearch = async () => {
    if (!input.trim() && !selectedImage) return;
    setIsSearching(true);
    setResult(null);
    setError(null);
    setShowReasoning(false);

    try {
      const userContent: any[] = [{ type: "text", text: input || "Please analyze this image and provide spiritual guidance." }];
      
      if (selectedImage) {
        userContent.push({
          type: "image_url",
          image_url: {
            url: selectedImage,
          },
        });
      }

      const systemPrompt = `You are a knowledgeable Islamic scholar and spiritual companion. Your goal is to provide comfort and guidance based strictly on the Quran and Sunnah. You must not offer personal opinions. You must be 'Adl' (just and balanced). When a user shares their feelings or an image, analyze the context (visual or textual) to provide a relevant verse from the Quran or a Hadith (in Arabic and English translation), the source, and a brief, gentle piece of advice that connects the scripture to their situation. Return the response as a valid JSON object with the following keys: 'arabic' (the Arabic text), 'english' (the English translation), 'source' (e.g., 'Surah Al-Baqarah 2:286'), 'advice' (the brief guidance), and 'reasoning' (a transparent explanation of exactly why this specific scripture was chosen for this query).${
        compareBible 
          ? " ALSO include a 'bible_comparison' object with keys: 'text' (a relevant Bible verse that shares a similar message), 'source' (e.g., 'Psalm 23:1'), and 'connection' (a brief sentence explaining the shared wisdom or theological difference, focusing on building bridges for reverts)." 
          : ""
      }`;

      // Use the PHP Proxy instead of client-side key
      // Use relative path to avoid CORS/Domain issues
      const response = await fetch('/openai_proxy.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: "gpt-4o",
          messages: [
            {
              role: "system",
              content: systemPrompt
            },
            { role: "user", content: userContent }
          ],
          max_tokens: 1000,
          response_format: { type: "json_object" },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const completion = await response.json();
      const content = completion.choices[0].message.content;
      if (content) {
        const parsedResult = JSON.parse(content);
        
        // Log the successful interaction
        logEvent({
          type: 'lifeline_query',
          input: input || (selectedImage ? '[Image Upload]' : ''),
          outcome: parsedResult.advice,
          details: {
            arabic: parsedResult.arabic,
            english: parsedResult.english,
            source: parsedResult.source,
            reasoning: parsedResult.reasoning,
          bible_comparison: parsedResult.bible_comparison
          }
        });

        setResult({
          id: 'ai-generated',
          tags: [],
          arabic: parsedResult.arabic,
          english: parsedResult.english,
          source: parsedResult.source,
          advice: parsedResult.advice,
          reasoning: parsedResult.reasoning,
          bible_comparison: parsedResult.bible_comparison
        });
        // Clear image after successful search
        setSelectedImage(null);
      } else {
         throw new Error('No response from AI');
      }

    } catch (err: any) {
      console.error(err);
      setError(err.message || 'An error occurred while seeking guidance.');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
      {/* Header */}
      <div className="p-6 pt-safe-top flex items-center gap-4 border-b border-slate-800 justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack}
            className="p-2 -ml-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              The Lifeline <Sparkles className="w-4 h-4 text-emerald-400" />
            </h2>
            <p className="text-xs text-slate-400">AI Spiritual Companion (Beta)</p>
          </div>
        </div>
        <button 
          onClick={() => setShowHistory(true)}
          className="p-2 text-slate-400 hover:text-emerald-400 transition-colors"
          title="Session History"
        >
          <History className="w-5 h-5" />
        </button>
      </div>

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-slate-900/95 z-50 p-6 flex flex-col animate-in fade-in duration-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <History className="w-5 h-5 text-emerald-400" /> Session Journal
            </h3>
            <button 
              onClick={() => setShowHistory(false)}
              className="p-2 bg-slate-800 rounded-full hover:bg-slate-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto space-y-4">
            {logs.length === 0 ? (
              <div className="text-center text-slate-500 mt-20">
                <p>No sessions recorded yet.</p>
              </div>
            ) : (
              logs.map(log => (
                <div key={log.id} className="bg-slate-800 p-4 rounded-xl border border-slate-700">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                    <span className="text-[10px] bg-slate-700 px-2 py-0.5 rounded text-emerald-400">
                      {log.type.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-white mb-2">"{log.input}"</p>
                  <div className="bg-slate-900/50 p-3 rounded-lg text-xs text-slate-300">
                    {log.outcome}
                  </div>
                </div>
              ))
            )}
          </div>
          
          {logs.length > 0 && (
            <button 
              onClick={clearLogs}
              className="mt-4 py-3 text-red-400 text-sm hover:bg-red-900/10 rounded-xl transition-colors"
            >
              Clear History
            </button>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 p-6 flex flex-col justify-center max-w-md mx-auto w-full">
        
        {!result && !isSearching && !error && (
          <div className="text-center space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-700 overflow-hidden">
              {profile.avatar ? (
                <img src={profile.avatar} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <img src={logo} alt="Habl Logo" className="w-10 h-10 object-contain opacity-80" />
              )}
            </div>
            <h3 className="text-2xl font-bold">
              {profile.name === 'Username' || profile.name === 'Musafir' ? 'What is weighing on your heart?' : `Salaam, ${profile.name.split(' ')[0]}.`}
            </h3>
            <p className="text-slate-400 leading-relaxed">
              {profile.name === 'Username' || profile.name === 'Musafir' 
                ? 'Tell me how you feel, or describe a situation.' 
                : 'I am here to listen. What is weighing on your heart today?'}
              <br/>
              I will search the Quran & Sunnah for guidance.
            </p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
             <div className="w-20 h-20 bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-900/50">
               <AlertCircle className="w-10 h-10 text-red-500" />
             </div>
             <h3 className="text-xl font-bold text-red-400">Connection Issue</h3>
             <p className="text-slate-400 leading-relaxed">
               {error}
             </p>
             <button 
               onClick={() => setError(null)}
               className="px-6 py-2 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-colors mt-4"
             >
               Try Again
             </button>
          </div>
        )}

        {/* Loading State */}
        {isSearching && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-emerald-400 font-medium animate-pulse">Consulting the Wisdom...</p>
          </div>
        )}

        {/* Result Card */}
        {result && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-3xl p-6 space-y-6 animate-in zoom-in duration-500">
            <div className="space-y-4 text-center">
              <p className="text-2xl font-serif leading-loose text-emerald-100" dir="rtl">
                {result.arabic}
              </p>
              <p className="text-emerald-400 text-sm font-medium">
                "{result.english}"
              </p>
              <p className="text-xs text-slate-500 uppercase tracking-widest">
                {result.source}
              </p>
            </div>
            
            <div className="bg-emerald-900/20 p-4 rounded-xl border border-emerald-900/50">
              <h4 className="text-emerald-400 font-bold text-sm mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Suggestion
              </h4>
              <p className="text-slate-300 text-sm leading-relaxed">
                {result.advice}
              </p>
            </div>

            {/* Bible Comparison (If requested) */}
            {result.bible_comparison && (
              <div className="bg-stone-100/10 p-4 rounded-xl border border-stone-500/30">
                <h4 className="text-stone-300 font-bold text-sm mb-2 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" /> Scriptural Connection
                </h4>
                <p className="text-stone-200 font-serif italic mb-2">
                  "{result.bible_comparison.text}"
                </p>
                <p className="text-xs text-stone-400 uppercase tracking-widest mb-3">
                  {result.bible_comparison.source}
                </p>
                <p className="text-stone-300 text-sm leading-relaxed border-t border-stone-500/30 pt-2">
                  <span className="text-stone-400 text-xs uppercase font-bold mr-2">Bridge:</span>
                  {result.bible_comparison.connection}
                </p>
              </div>
            )}

            {/* Glass Ledger Transparency */}
            {result.reasoning && (
              <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
                <button 
                  onClick={() => setShowReasoning(!showReasoning)}
                  className="w-full flex items-center justify-between text-xs text-slate-500 hover:text-emerald-400 transition-colors"
                >
                  <span className="flex items-center gap-2 font-mono uppercase tracking-wider">
                    <Eye className="w-3 h-3" /> Glass Ledger
                  </span>
                  <span>{showReasoning ? 'Hide' : 'Show'} Logic</span>
                </button>
                
                {showReasoning && (
                  <div className="mt-3 pt-3 border-t border-slate-700/50 animate-in slide-in-from-top-2 duration-300">
                    <p className="text-xs text-slate-400 font-mono leading-relaxed">
                      <span className="text-emerald-500/50">AI_REASONING:</span> {result.reasoning}
                    </p>
                  </div>
                )}
              </div>
            )}

            <button 
              onClick={() => { setInput(''); setResult(null); }}
              className="w-full py-3 text-slate-400 text-sm hover:text-white transition-colors"
            >
              Ask about something else
            </button>
          </div>
        )}

        {/* No Result Fallback */}
        {/* If search finishes but no match found (omitted for brevity in demo, assuming happy path or generic fallback later) */}

      </div>

      {/* Input Area */}
      <div className="p-6 bg-slate-900 border-t border-slate-800 safe-area-bottom">
        <div className="max-w-md mx-auto relative">
          
          {/* Image Preview */}
          {selectedImage && (
            <div className="absolute bottom-full mb-4 left-0 right-0 animate-in slide-in-from-bottom-2 duration-300">
              <div className="relative w-fit bg-slate-800 p-2 rounded-xl border border-slate-700">
                <img src={selectedImage} alt="Selected" className="h-20 w-auto rounded-lg" />
                <button 
                  onClick={() => setSelectedImage(null)}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageSelect}
            className="hidden"
          />

          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder={selectedImage ? "Add context about this image..." : "e.g., I feel lonely, I am angry..."}
            className="w-full bg-slate-800 text-white placeholder:text-slate-500 rounded-2xl py-4 pl-5 pr-24 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
          />
          <div className="absolute right-2 top-2 flex gap-1">
             <button 
               onClick={() => setCompareBible(!compareBible)}
               className={`p-2 rounded-xl transition-colors ${compareBible ? 'text-amber-400 bg-amber-900/20' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
               title="Bible Comparison (For Reverts)"
             >
               <BookOpen className="w-5 h-5" />
             </button>
             <button 
               onClick={() => fileInputRef.current?.click()}
               className={`p-2 rounded-xl transition-colors ${selectedImage ? 'text-emerald-400 bg-emerald-900/20' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`} 
               title="Halal Scanner (Analyze Image)"
             >
               <Camera className="w-5 h-5" />
             </button>
             <button 
               onClick={handleSearch}
               disabled={(!input.trim() && !selectedImage) || isSearching}
               className="p-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
             >
               {isSearching ? <Search className="w-5 h-5 animate-pulse" /> : <Send className="w-5 h-5" />}
             </button>
          </div>
        </div>
        <p className="text-center text-[10px] text-slate-600 mt-4">
          Powered by Habl Intelligence. Guidance is from Allah alone.
        </p>
      </div>
    </div>
  );
}
